import { useState } from "react";
import { useQuery } from "@apollo/client";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { GetCallDocument, GetCallQuery, GetSideviewerCallsByShipDocument } from "__generated__/view/calls/graphql/call.queries.generated";
import { GetCargoForCallSideviewerQuery } from "__generated__/view/cargos/graphql/cargo.queries.generated";
import { ChildProps } from "types/commons";

export type CallFromCallSideviewer = ChildProps<GetCallQuery, "call">;
export type CargoFromCallSideviewer = ChildProps<GetCargoForCallSideviewerQuery, "cargo">
export type CargoActionFromCallSideviewer = CargoFromCallSideviewer["actions"][number];

const useCallSideviewer = ({
	defaultCallId,
	showFutureCalls = false,
}: {
	defaultCallId: number | null;
	showFutureCalls?: boolean;
}) => {
	const [ selectedId, setSelectedId ] = useState<number | null>(defaultCallId);

	const { data, previousData, loading, refetch } = useQuery(GetCallDocument, {
		variables: {
			id: selectedId ?? 0,
		},
		skip: !selectedId,
	});

	const fetchCall = (id: number) => {
		if (id === selectedId) {
			refetch();
		} else {
			setSelectedId(id);
		}
	};

	usePropertyChangeEffect(() => {
		setSelectedId(defaultCallId);
	}, [ defaultCallId ]);

	const selectedCall = data?.call ?? previousData?.call ?? null;

	const shipId = selectedCall?.voyage?.ship?.id;

	const { data: callsData, loading: callsLoading } = useQuery(GetSideviewerCallsByShipDocument, {
		variables: {
			shipId: shipId ?? 0,
		},
		skip: !showFutureCalls || !shipId,
	});

	return {
		selectedCall,
		calls: showFutureCalls ? (callsData?.callsByShip ?? []) : (selectedCall?.voyage?.calls ?? []),
		loading: loading || (showFutureCalls && callsLoading),
		fetchCall,
	};
};

export default useCallSideviewer;
